import { BaseComponent } from '../../base-component';
import { ArticleOptionsServices } from '../../global';
import { decodeHtml } from '../../_common/client/decode-html';
import { ARTICLE_OPTIONS_CHANGED_EVENT } from './events';
import { eventQBus } from '@otto-ec/global-resources/event-q-bus';

export class ArticleOptionsComponent extends BaseComponent {
    public rootSelector = '.js_pdp_article-options';
    private deliveryOnDemand = false;

    public async onHtmlLoaded(): Promise<void> {
        if (!this.isPresent()) {
            // reset basket selected services, if nothing loaded
            this.emitEmptyCheckboxesState();
            return;
        }
        this.deliveryOnDemand = this.getRootElement().dataset.variationDeliveryOnDemand === 'true';

        // make page impression tracking with html decoded content
        const pvTrackString = decodeHtml(this.getRootElement().dataset.pageViewHtmlEncodedString || '');
        this.trackView({
            order_ServiceAll: pvTrackString,
            order_ServiceId: this.getServiceIdsForTracking(),
            product_ServiceCount: this.getAvailableArticleOptions(),
        });

        // emit for basket, because there is probably something preselected.
        this.emitSelectedCheckboxesState();

        this.getCheckBoxes().forEach((chkBox: HTMLInputElement) => {
            // update immediately, because just loaded and some options are preselected
            this.updateWarningState(chkBox);

            chkBox.addEventListener('change', () => this.onChkBoxChanged(chkBox));
        });
    }

    private onChkBoxChanged(chkBox: HTMLInputElement) {
        this.updateWarningState(chkBox);
        this.processTracking(chkBox);
        this.emitSelectedCheckboxesState();
        this.handleDeliveryOnDemand();
    }

    private processTracking(chkBox: HTMLInputElement) {
        const notApplicableForDesiredDate = chkBox.dataset.deliveryForDesiredDate === 'false';
        const trackSlowFeature = chkBox.checked && this.deliveryOnDemand && notApplicableForDesiredDate;
        const serviceId = this.getServiceIdOfCheckBox(chkBox);

        if (trackSlowFeature) {
            const availabilityMessageSlow = this.getRootElement().dataset.availabilityMessageSlow;
            this.trackEvent({
                order_Service: this.getLabelOfCheckBox(chkBox),
                order_ServiceActivity: 'select',
                order_ServicePage: 'Artikeldetailseite',
                product_AvailabilityView: availabilityMessageSlow,
                order_ServiceId: serviceId,
                product_ServiceCount: this.getAvailableArticleOptions(),
            });
        } else {
            this.trackEvent({
                order_Service: this.getLabelOfCheckBox(chkBox),
                order_ServiceActivity: chkBox.checked ? 'select' : 'deselect',
                order_ServicePage: 'Artikeldetailseite',
                order_ServiceId: serviceId,
                product_ServiceCount: this.getAvailableArticleOptions(),
            });
        }
    }

    /**
     * sends event to ordering, so ordering (any articleOptionsChangedaybe others) knows what services are selected.
     */
    private emitSelectedCheckboxesState(): void {
        eventQBus.emit(ARTICLE_OPTIONS_CHANGED_EVENT, this.getSelectedServicesForOrdering());
    }

    /**
     * sends empty checkbox state, to reset
     */
    private emitEmptyCheckboxesState(): void {
        eventQBus.emit(ARTICLE_OPTIONS_CHANGED_EVENT, this.createServicesChangedEvent([]));
    }

    private getCheckBoxes(): HTMLInputElement[] {
        return this.getChildElements<HTMLInputElement>('.js_pdp_article-options__checkbox-input');
    }

    private updateWarningState(chkBox: HTMLInputElement): void {
        const notApplicableForDesiredDate = chkBox.dataset.deliveryForDesiredDate === 'false';
        const showWarning = chkBox.checked && this.deliveryOnDemand && notApplicableForDesiredDate;
        this.setWarningVisibility(chkBox, showWarning);
    }

    private setWarningVisibility(chkBox: HTMLInputElement, shouldShowWarning: boolean): void {
        const warningElem = chkBox.parentElement
            ?.closest('.js_pdp_article-options__item')
            ?.querySelector<HTMLDivElement>('.js_pdp_article-options__warning');
        if (!warningElem) {
            throw new Error('cant find warning element for input' + chkBox.id);
        }

        if (shouldShowWarning) {
            warningElem.classList.remove('pl_hidden');
        } else {
            warningElem.classList.add('pl_hidden');
        }
    }

    private getLabelOfCheckBox(chkBox: HTMLInputElement): string {
        return chkBox.parentElement?.querySelector('label')?.innerText || '';
    }

    private getServiceIdOfCheckBox(chkBox: HTMLInputElement): string {
        return chkBox.getAttribute('data-id') || '';
    }

    private getSelectedServicesForOrdering(): ArticleOptionsServices {
        const serviceUuids = this.getCheckBoxes()
            .filter(chkBox => chkBox.checked)
            .map(chkBox => chkBox.dataset.id || '');

        return this.createServicesChangedEvent(serviceUuids);
    }

    private getServiceIdsForTracking(): string {
        return this.getCheckBoxes()
            .map(chkBox => chkBox.dataset.id || '')
            .join('$');
    }

    private getAvailableArticleOptions(): number {
        return this.getCheckBoxes().length;
    }

    private createServicesChangedEvent(serviceUuids: string[]): { services: { uuid: string }[] } {
        const serviceDefinitions = serviceUuids.map(uuid => ({ uuid: uuid }));
        return { services: serviceDefinitions };
    }

    // [FT5-2317]: references element in delivery component
    private handleDeliveryOnDemand(): void {
        const dodAvailable =
            this.getCheckBoxes()
                .filter(chkBox => chkBox.checked)
                .map(chkBox => chkBox.dataset.deliveryForDesiredDate)
                .filter(state => state === 'false').length == 0;

        if (dodAvailable) {
            // [FT5-2317]: references element in delivery component
            const inactive = document.querySelector('.js_pdp_delivery__dod-wrapper__inactive');
            const active = document.querySelector('.js_pdp_delivery__dod-wrapper__active');
            if (inactive && active) {
                inactive.classList.add('pl_hidden');
                active.classList.remove('pl_hidden');
            }
        } else {
            // [FT5-2317]: references element in delivery component
            const inactive = document.querySelector('.js_pdp_delivery__dod-wrapper__inactive');
            const active = document.querySelector('.js_pdp_delivery__dod-wrapper__active');
            if (inactive && active) {
                inactive.classList.remove('pl_hidden');
                active.classList.add('pl_hidden');
            }
        }
    }
}
